import React from "react";
import get from "lodash/get";
import { GlideCarousel } from "../atoms/glide";
import { ImageFallback } from "./../basic/image-fallback.js";
import { LazyLoadImages, wrapCollectionLayout, ResponsiveImage } from "@quintype/components";
import { CustomLink } from "../custom-link";
import { CollectionName, generateSectionPageRoutesDupe } from "../atoms/collection-name/index.js";
import { getNumberOfCollectionToShow, getScrollSpeed, isEager, getRowMetadata } from "../../utils";
import { breakpoint } from "../../../utils/breakpoint";
import PropTypes from "prop-types";

import "./slider-collection-of-collections.module.css";

const CollectionCard = ({ story, collection, associatedMetadata, aspectRatio = [4, 3], route, eager = false }) => {
  const coverImage = get(collection, ["metadata", "cover-image", "cover-image-s3-key"], "");

  const coverImageMetaData = get(collection, ["metadata", "cover-image", "cover-image-metadata"], {});

  function showDefaultImage() {
    if (!coverImage) {
      return <ImageFallback styleName="aspect-ratio" story={story} />;
    }

    return (
      <figure styleName="aspect-ratio">
        <LazyLoadImages margin={"0px"}>
          <ResponsiveImage
            slug={coverImage}
            metadata={coverImageMetaData}
            alt={collection.name}
            aspectRatio={[4, 3]}
            defaultWidth={400}
            widths={[300, 400]}
            sizes="(max-width: 480px) 98vw, (max-width: 300px) 48vw, 100vw"
            imgParams={{ auto: ["format", "compress"] }}
            eager={eager}
          />
        </LazyLoadImages>
      </figure>
    );
  }

  return (
    <div className="collection-card ">
      <CustomLink href={route["sectionPath"] ? route["sectionPath"] : `/collection/${collection.slug}`}>
        <div styleName="collection-card-image" data-testid="collection-card-image">
          {showDefaultImage()}
        </div>
        <div styleName="collection-card-content">
          <h3 styleName="collection-card-headline" data-testid="collection-card-headline">
            {collection.name}
          </h3>
        </div>
      </CustomLink>
    </div>
  );
};

CollectionCard.propTypes = {
  collection: PropTypes.object,
  associatedMetadata: PropTypes.object,
  aspectRatio: PropTypes.array,
  route: PropTypes.object,
  eager: PropTypes.bool,
  story: PropTypes.object
};

function getSlidesEager(index, eager) {
  if (breakpoint("tablet")) {
    return index === 0 ? eager : false;
  }
  return index >= 0 && index < 4 ? eager : false;
}

function getRoute(config, item) {
  if (get(item, ["metadata", "section"])) {
    const [firstRoute] = generateSectionPageRoutesDupe(config, {}, item) || [];
    return firstRoute;
  }
  return {
    sectionPath: `/collection/${item.slug}`
  };
}

function SliderCollectionOfCollectionsBase(props) {
  const collections = get(props, ["collection", "items"], []).filter(item => item.type === "collection");
  const { collection: collection = true, metadata: associatedMetadata = {}, index, config } = props;
  const eager = isEager(index, true);
  const pbAheadConfig = getRowMetadata(config, associatedMetadata);

  const { show_collection_title: collectionName = true } = pbAheadConfig;
  const paginationValue = get(props, ["associatedMetadata", "enable pagination - dot or dash"], "dot").toLowerCase();
  let resizeDone = false;

  const resize = () => {
    if (resizeDone === false) {
      global.dispatchEvent(new global.Event("resize"));
      resizeDone = true;
    }
  };

  const desktop = {
    afterSlide: resize,
    arrow: pbAheadConfig["show_arrows"],
    perView: 4,
    autoplay: getScrollSpeed(pbAheadConfig),
    gap: 24,
    dot: paginationValue,
    items: collections.slice(0, getNumberOfCollectionToShow(pbAheadConfig["number_of_collections_to_show"]))
  };
  const mobile = {
    afterSlide: resize,
    arrow: true,
    perView: 1,
    dot: paginationValue,
    autoplay: getScrollSpeed(associatedMetadata),
    items: collections.slice(0, getNumberOfCollectionToShow(associatedMetadata["number_of_collections_to_show"]))
  };

  const options = breakpoint("tablet") ? { ...mobile } : { ...desktop };
  const SliderWrapper = (
    <GlideCarousel {...options} styleName="slider-collection-of-collections">
      {collections
        .slice(0, getNumberOfCollectionToShow(associatedMetadata["number_of_collections_to_show"]))
        .map((item, index) => {
          const initialVisibleSlidesEager = getSlidesEager(index, eager);
          return (
            <CollectionCard
              collection={item}
              aspectRatio={[4, 3]}
              associatedMetadata={associatedMetadata}
              key={item.id}
              route={getRoute(props.config, item)}
              eager={initialVisibleSlidesEager}
            />
          );
        })}
    </GlideCarousel>
  );
  return (
    <div className="wrapper">
      <div data-testid="slider-collection-of-collection">
        {collectionName ? <CollectionName name={collection.name} config={config} section={collection} /> : null}
        {SliderWrapper}
      </div>
    </div>
  );
}
SliderCollectionOfCollectionsBase.propTypes = {
  config: PropTypes.object,
  metadata: PropTypes.object,
  collection: PropTypes.object,
  index: PropTypes.number
};

export const SliderCollectionOfCollections = wrapCollectionLayout(SliderCollectionOfCollectionsBase);
